import React, { useState } from 'react';
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, TableSortLabel, CircularProgress, Box, Chip, Typography } from '@mui/material';
import { TabPanel} from "@mui/lab";
import { SearchOutlined } from "@mui/icons-material";
import styled from "@emotion/styled";
import useSortableTable from "../utils/useSortableTable";

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  padding: theme.spacing(0),
}));

const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const ResourceUtilizationTable = ({ data, loading, hasFetched }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { sorted, order, handleSort } = useSortableTable(data, {
    employeeName: r => r.employeeName,
    department: r => r.department,
    capacity: r => r.capacity || 0,
    timeTracked: r => Number(r.timeTracked) || 0,
    totalUtilization: r => Number(r.totalUtilization) || 0,
    billableHours: r => Number(r.billableHours) || 0,
    nonBillableHours: r => Number(r.nonBillableHours) || 0,
    billableUtilization: r => Number(r.billableUtilization) || 0,
    month: r => r.month || '',
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    // BUG FIX (B14): radix 10, not 25 — see RunnitRetainers for full note.
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <StyledTabPanel value="resources">
      <Paper>
        <TableContainer>
          {loading ? (
            <LoadingBox>
              <CircularProgress />
            </LoadingBox>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow key="header">
                  <TableCell sortDirection={order.key === 'employeeName' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'employeeName'} direction={order.key === 'employeeName' ? order.dir : 'asc'} onClick={() => handleSort('employeeName')}>Employee Name</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={order.key === 'department' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'department'} direction={order.key === 'department' ? order.dir : 'asc'} onClick={() => handleSort('department')}>Department</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={order.key === 'capacity' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'capacity'} direction={order.key === 'capacity' ? order.dir : 'asc'} onClick={() => handleSort('capacity')}>Capacity</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'timeTracked' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'timeTracked'} direction={order.key === 'timeTracked' ? order.dir : 'asc'} onClick={() => handleSort('timeTracked')}>Time Tracked</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'totalUtilization' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'totalUtilization'} direction={order.key === 'totalUtilization' ? order.dir : 'asc'} onClick={() => handleSort('totalUtilization')}>Total Utilization %</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'billableHours' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'billableHours'} direction={order.key === 'billableHours' ? order.dir : 'asc'} onClick={() => handleSort('billableHours')}>Billable Hours</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'nonBillableHours' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'nonBillableHours'} direction={order.key === 'nonBillableHours' ? order.dir : 'asc'} onClick={() => handleSort('nonBillableHours')}>Non-Billable Hours</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'billableUtilization' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'billableUtilization'} direction={order.key === 'billableUtilization' ? order.dir : 'asc'} onClick={() => handleSort('billableUtilization')}>Billable Utilization %</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={order.key === 'month' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'month'} direction={order.key === 'month' ? order.dir : 'asc'} onClick={() => handleSort('month')}>Month</TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((resource) => (
                  <TableRow key={`${resource.id}-${resource.month || 'total'}`} hover>
                    <TableCell>{resource.employeeName}</TableCell>
                    <TableCell>{resource.department}</TableCell>
                    <TableCell>{resource.capacity}</TableCell>
                    <TableCell align="right">{resource.timeTracked || 0}</TableCell>
                    <TableCell align="right">
                      <Chip label={`${resource.totalUtilization || 0}%`} size="small" color={(resource.totalUtilization || 0) > 85 ? "success" : (resource.totalUtilization || 0) > 70 ? "primary" : "warning"} />
                    </TableCell>
                    <TableCell align="right">{resource.billableHours || 0}</TableCell>
                    <TableCell align="right">{resource.nonBillableHours || 0}</TableCell>
                    <TableCell align="right">
                      <Chip label={`${resource.billableUtilization || 0}%`} size="small" color={(resource.billableUtilization || 0) > 85 ? "success" : (resource.billableUtilization || 0) > 70 ? "primary" : "warning"} />
                    </TableCell>
                    <TableCell>{resource.month || ""}</TableCell>
                  </TableRow>
                ))}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <EmptyStateBox>
                        <SearchOutlined />
                        <Typography variant="body1">{hasFetched ? 'No resources match the current filters' : 'Click Generate Report to load data'}</Typography>
                      </EmptyStateBox>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {!loading && data.length > 25 && <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={data.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />}
      </Paper>
    </StyledTabPanel>
  );
};

export default ResourceUtilizationTable;